
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

// Temporary favorites storage in localStorage until database table is ready
const FAVORITES_KEY = 'user-favorites';

const getFavoritesFromStorage = (userId: string): string[] => {
  try {
    const stored = localStorage.getItem(`${FAVORITES_KEY}-${userId}`);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveFavoritesToStorage = (userId: string, favorites: string[]) => {
  try {
    localStorage.setItem(`${FAVORITES_KEY}-${userId}`, JSON.stringify(favorites));
  } catch (error) {
    console.error('Failed to save favorites:', error);
  }
};

export const useFavoriteServer = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ serverId, isFavorite }: { serverId: string; isFavorite: boolean }) => {
      if (!user) throw new Error('User not authenticated');

      const currentFavorites = getFavoritesFromStorage(user.id);
      
      let newFavorites: string[];
      if (isFavorite) {
        // Add to favorites
        newFavorites = [...currentFavorites, serverId];
      } else {
        // Remove from favorites
        newFavorites = currentFavorites.filter(id => id !== serverId);
      }
      
      saveFavoritesToStorage(user.id, newFavorites);
      return newFavorites;
    },
    onSuccess: (_, { isFavorite }) => {
      queryClient.invalidateQueries({ queryKey: ['servers'] });
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      
      toast({
        title: isFavorite ? "Ajouté aux favoris" : "Retiré des favoris",
        description: isFavorite ? "Serveur ajouté à vos favoris !" : "Serveur retiré de vos favoris",
      });
    },
    onError: (error) => {
      console.error('Favorite error:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la modification des favoris.",
        variant: "destructive",
      });
    },
  });
};

// Hook to check if a server is favorited
export const useIsFavorite = (serverId: string): boolean => {
  const { user } = useAuth();
  
  if (!user) return false;
  
  const favorites = getFavoritesFromStorage(user.id);
  return favorites.includes(serverId);
};
